import React from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { Translate } from '@hydra/common/components';
import get from 'lodash/get';

import FilterContainer from '@hydra/routes/crawler/components/FilterContainer';
import FilterCheckboxes from '@hydra/routes/crawler/components/FilterCheckboxes';
import FilterString from '@hydra/routes/crawler/components/FilterString';
import { withFilters } from '@hydra/routes/crawler/components/withFilters';
import CrawlerTable from '@hydra/routes/crawler/components/CrawlerTable';
import HttpWithinHttpsRow from '../HttpWithinHttpsRow';

const ASSET_TYPE_FILTERS = [
  { identifier: 'img', value: 'img' },
  { identifier: 'js', value: 'js' },
  { identifier: 'css', value: 'css' },
  { identifier: 'misc', value: 'misc' }
];

export class HttpWithinHttpsTable extends React.Component {
  static propTypes = {
    project: PropTypes.shape({
      crawl: PropTypes.shape({
        httpStatusCategory: PropTypes.shape({
          countTypesHttpInHttps: PropTypes.arrayOf(
            PropTypes.shape({
              count: PropTypes.number,
              severity: PropTypes.number,
              type: PropTypes.string
            })
          ),
          httpInHttps: PropTypes.shape({
            count: PropTypes.number,
            edges: PropTypes.arrayOf(
              PropTypes.shape({
                node: PropTypes.object
              })
            )
          })
        })
      })
    }).isRequired,
    offset: PropTypes.number,
    limit: PropTypes.number
  }

  render () {
    const toggles = get(this.props.project, 'crawls.edges[0].node.countTypesHttpInHttps', [])
      .map(v => ({ identifier: v.type, ...v }));
    return (
      <>
        <FilterContainer>
          <FilterCheckboxes
            onChange={this.props.onGraphQLCheckboxInputChange('typeFilters', toggles, false)}
            checkedItems={this.props.filters.typeFilters}
            groups={toggles}
            name='typeFilters'
            label={<Translate content='crawler_number_of_assets' />}
          />

          <FilterCheckboxes
            onChange={this.props.onGraphQLCheckboxInputChange('assetTypeFilters', ASSET_TYPE_FILTERS, false)}
            checkedItems={this.props.filters.assetTypeFilters}
            groups={ASSET_TYPE_FILTERS}
            name='assetTypeFilters'
            label={<Translate content='crawler_http_asset_type' />}
          />

          <FilterString
            onChange={this.props.onGraphQLInputChange('assetUrl')}
            value={this.props.filters.assetUrl}
            name='assetUrl'
            label={<Translate content='crawler_http_asset_url' />}
          />

          <FilterString
            onChange={this.props.onGraphQLInputChange('sourceUrl')}
            value={this.props.filters.sourceUrl}
            name='sourceUrl'
            label={<Translate content='crawler_https_source_page_url' />}
          />
        </FilterContainer>
        <CrawlerTable {...this.props} category='httpStatusCategory' section='httpInHttps'>
          <CrawlerTable.TableHead>
            <Translate content='crawler_number_of_assets' size='1' />
            <Translate content='crawler_http_asset_type' size='2' />
            <Translate content='crawler_http_asset_url' size='4' />
            <Translate content='crawler_https_source_page_url' size='5' />
          </CrawlerTable.TableHead>
          <CrawlerTable.Body component={HttpWithinHttpsRow} />
        </CrawlerTable>
      </>
    );
  }
}

const I18nHttpWithinHttpsTableWithFilters = (withFilters)(HttpWithinHttpsTable);

export default createRefetchContainer(
  I18nHttpWithinHttpsTableWithFilters,
  {
    project: graphql`
      fragment HttpWithinHttpsTable_project on Project
      @argumentDefinitions(
        offset: {type: "Int", defaultValue: 0},
        limit: {type: "Int", defaultValue: 20},
        typeFilters: {type: "[String]", defaultValue: []},
        assetTypeFilters: {type: "[String]", defaultValue: ["img","js","css","misc"] },
        assetUrl: {type: "TextFilters", defaultValue: { operator: AND, values: [] }},
        sourceUrl: {type: "TextFilters", defaultValue: { operator: AND, values: [] }}
      ) {
        host,
        crawls (offset: 0, limit: 1) {
          edges {
            node {
              countTypesHttpInHttps(assetTypeFilters: $assetTypeFilters, assetUrl: $assetUrl, sourceUrl: $sourceUrl) {
                type,
                severity,
                count
              }
              httpInHttps (offset: $offset, limit: $limit, typeFilters: $typeFilters, assetTypeFilters: $assetTypeFilters, assetUrl: $assetUrl, sourceUrl: $sourceUrl) {
                count,
                edges {
                  node {
                    ...HttpWithinHttpsRow_issue
                  }
                }
              }
            }
          }
        }
      }
    `
  },
  graphql`
      query HttpWithinHttpsTable_RefetchQuery(
        $host: String!
        $limit: Int,
        $offset: Int,
        $assetTypeFilters: [String],
        $typeFilters: [String],
        $sourceUrl: TextFilters,
        $assetUrl: TextFilters) {,
        project (host: $host) {
          ...HttpWithinHttpsTable_project @arguments(
            offset: $offset,
            limit: $limit,
            assetTypeFilters: $assetTypeFilters,
            typeFilters: $typeFilters,
            assetUrl: $assetUrl,
            sourceUrl: $sourceUrl
          )
        }
      }
    `
);
